/**
 * Block shapes: a solid block can be put down whole, or cut to a slope,
 * without spending a tile id on each way (see Grid.shapes). The shape is a
 * small code stored beside the tile:
 *
 * - 0: the whole block;
 * - 1-4: a wedge, one corner (CORNER_DIRS) cut away to a 45 degree slope;
 * - 5-12: a gentle slope, 2:1: one tile of a two-tile run (the corner it cuts
 *   toward, and whether it's the lower or the upper tile of the run);
 * - 13-20: a steep slope, 1:2, the same way round.
 *
 * Every shape is convex, so the collision and drawing paths that take a
 * wedge's outline take them all. The set is closed under quarter turns
 * (a gentle slope turned on its side is a steep one), so a shape chosen on
 * a ship means the same on a rock that turns (see turnShape).
 */
import { CORNER_DIRS, isShaped, isSolid, shapeAt, tileAt, tileOutline, type Grid } from "./tiles";
import { groundShape, type Ground, type Pt, type TileShape } from "./world/contour";

export type { Pt, TileShape } from "./world/contour";

export const SHAPE_FULL = 0;
/** Shape codes run 0..SHAPE_COUNT-1. */
export const SHAPE_COUNT = 21;

type Kind = "wedge" | "gentle" | "steep";

/** What a shape code means: its kind, the corner it cuts toward, and (slopes) which tile of the run it is. */
function decodeShape(s: number): { kind: Kind; corner: number; half: number } | null {
  if (s <= 0 || s >= SHAPE_COUNT) return null;
  if (s <= 4) return { kind: "wedge", corner: s - 1, half: 0 };
  if (s <= 12) return { kind: "gentle", corner: (s - 5) >> 1, half: (s - 5) & 1 };
  return { kind: "steep", corner: (s - 13) >> 1, half: (s - 13) & 1 };
}

export function shapeCode(kind: Kind, corner: number, half = 0): number {
  const c = ((corner % 4) + 4) % 4;
  return kind === "wedge" ? 1 + c : (kind === "gentle" ? 5 : 13) + c * 2 + (half & 1);
}

/**
 * The outlines in the frame where the cut corner is the top-left one (the
 * slope rises to the right, solid below it), counter-clockwise in the tile's
 * [0, 1] square. Gentle: the lower tile is the thin end. Steep: the lower
 * tile is the thick end.
 */
const CANON: Record<Kind, Pt[][]> = {
  wedge: [[{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }]],
  gentle: [
    [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 0.5 }],
    [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 0.5 }],
  ],
  steep: [
    [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0.5, y: 1 }],
    [{ x: 0.5, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }],
  ],
};

/** The outline of shape `s` in the tile's own [0, 1] square, counter-clockwise and convex; null for a whole block (or no such shape). */
export function shapeOutline(s: number): Pt[] | null {
  const d = decodeShape(s);
  if (!d) return null;
  if (d.kind === "wedge") return tileOutline(1 << d.corner, 0);
  const [sx, sy] = CORNER_DIRS[d.corner]!;
  // Mirror the top-left frame onto this corner; one mirror turns the winding round.
  const poly = CANON[d.kind][d.half]!.map((p) => ({ x: sx < 0 ? p.x : 1 - p.x, y: sy > 0 ? p.y : 1 - p.y }));
  return (sx < 0) !== (sy > 0) ? poly.reverse() : poly;
}

const same = (a: Pt[], b: Pt[]): boolean => {
  if (a.length !== b.length) return false;
  const key = (p: Pt) => `${p.x.toFixed(4)},${p.y.toFixed(4)}`;
  const sa = a.map(key).sort();
  const sb = b.map(key).sort();
  return sa.every((k, i) => k === sb[i]);
};

/** shape -> the shape it is after one quarter turn counter-clockwise, worked out once by turning its outline and finding the match. */
const TURNED: number[] = (() => {
  const out = [0];
  for (let s = 1; s < SHAPE_COUNT; s++) {
    const turned = shapeOutline(s)!.map((p) => ({ x: 1 - p.y, y: p.x }));
    let found = -1;
    for (let t = 1; t < SHAPE_COUNT && found < 0; t++) if (same(shapeOutline(t)!, turned)) found = t;
    if (found < 0) throw new Error(`shape ${s} turned is no shape`);
    out.push(found);
  }
  return out;
})();

/** Shape `s` turned by `q` quarter turns counter-clockwise (see turnAt): a wedge's corner moves round; a gentle slope on its side is a steep one. */
export function turnShape(s: number, q: number): number {
  let out = s;
  for (let k = 0; k < (((q % 4) + 4) % 4); k++) out = TURNED[out] ?? 0;
  return out;
}

/** The slope's direction, by the corner it cuts toward (see CORNER_DIRS). */
const TOWARD = ["down-left", "down-right", "up-right", "up-left"] as const;

/** What a shape is called, for the status line: "wedge, sloping up-right", "gentle slope up-right, lower". */
export function shapeName(s: number): string {
  const d = decodeShape(s);
  if (!d) return "whole block";
  if (d.kind === "wedge") return `wedge, sloping ${TOWARD[d.corner]}`;
  return `${d.kind} slope ${TOWARD[d.corner]}, ${d.half ? "upper" : "lower"} tile`;
}

/** The corner a fresh shape of each kind cuts toward (up-right: a bow pointing forward), and the order T turns through them. */
const FIRST_CORNER = 2;
const CORNER_ORDER = [0, 1, 2, 3].map((k) => (FIRST_CORNER + k) % 4);

/**
 * Every shape a block that takes shapes can be put down in, in the order T
 * turns through them: whole, the four wedges, the gentle slopes (lower tiles
 * then upper), then the steep ones.
 */
export const SHAPE_ORDER: readonly number[] = [
  SHAPE_FULL,
  ...CORNER_ORDER.map((c) => shapeCode("wedge", c)),
  ...CORNER_ORDER.map((c) => shapeCode("gentle", c, 0)),
  ...CORNER_ORDER.map((c) => shapeCode("gentle", c, 1)),
  ...CORNER_ORDER.map((c) => shapeCode("steep", c, 0)),
  ...CORNER_ORDER.map((c) => shapeCode("steep", c, 1)),
];

/** Is `s` a shape code a player may ask for? */
export const isShape = (s: unknown): s is number => typeof s === "number" && Number.isInteger(s) && s >= 0 && s < SHAPE_COUNT;

/** Is the edge from `a` to `b` along one side of the tile's square? */
const onSide = (a: Pt, b: Pt): boolean => (a.x < 1e-6 && b.x < 1e-6) || (a.x > 1 - 1e-6 && b.x > 1 - 1e-6) || (a.y < 1e-6 && b.y < 1e-6) || (a.y > 1 - 1e-6 && b.y > 1 - 1e-6);

/** Shape `s` as a tile shape (see TileShape): its outline as the one piece, its slope (every edge not along a side of the square) as the surface. Null for a whole block. */
export function shapeTile(s: number): TileShape | null {
  const outline = shapeOutline(s);
  if (!outline) return null;
  const surface: [Pt, Pt][] = [];
  outline.forEach((a, i) => {
    const b = outline[(i + 1) % outline.length]!;
    if (!onSide(a, b)) surface.push([a, b]);
  });
  return { pieces: [outline], surface };
}

/**
 * How tile (x, y) of `g` is shaped, if it isn't a plain square (see
 * TileShape): ground smoothed to the ground round it (see groundShape;
 * `ground` says what counts as ground), a built block's own shape (see
 * Grid.shapes), or, for an open tile (empty, a back wall, a ladder, a
 * plant), the slivers of ground next door that reach into it. Null for a
 * square tile, or an open one with no ground about. What characters and
 * ships collide with, and what the client draws.
 */
export function tileShape(g: Grid, x: number, y: number, ground?: Ground): TileShape | null {
  const t = tileAt(g, x, y);
  if (isShaped(t) || !isSolid(t)) return groundShape(g, x, y, ground);
  return shapeTile(shapeAt(g, y * g.w + x));
}
